<template>
  <div class="badge-large">
    <div v-if="loading" class="badge-large__status" role="status">
      Loading: {{ Math.floor(progress) }}%
    </div>
    <template v-else>
      <Badge3d
        v-if="modelBuffer && !modelError"
        :model-buffer="modelBuffer"
        @error="modelError = true"
      />
      <div v-else-if="imageSrc && !imageError" class="badge-large__image">
        <img :src="imageSrc" :alt="name" @error="imageError = true" />
      </div>
      <div v-else class="badge-large__status" role="status">
        <span>徽章加载失败</span>
        <button type="button" @click="load">重新加载</button>
      </div>
      <BadgeCelebration
        v-if="animationData && !imageError && (modelBuffer || imageSrc)"
        :animation-data="animationData"
      />
    </template>
  </div>
</template>

<script setup>
import {
  computed,
  defineProps,
  onBeforeUnmount,
  ref,
  shallowRef,
  watch,
} from "vue";
import Badge3d from "./Badge3d.vue";
import BadgeCelebration from "./BadgeCelebration.vue";
import { loadBadgeAssets } from "@/utils/badge-assets";

const props = defineProps({
  badgeStyle: { type: String, default: "DEFAULT" },
  url: { type: String, default: "" },
  fallbackUrl: { type: String, default: "" },
  name: { type: String, default: "徽章" },
});
const loading = ref(true);
const progress = ref(0);
const modelBuffer = shallowRef(null);
const animationData = shallowRef(null);
const objectUrl = ref("");
const modelError = ref(false);
const imageError = ref(false);
const imageSrc = computed(() => objectUrl.value || props.fallbackUrl);
let controller;

function release() {
  controller?.abort();
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value);
  objectUrl.value = "";
}

async function load() {
  release();
  const request = new AbortController();
  controller = request;
  loading.value = true;
  progress.value = 0;
  modelBuffer.value = null;
  animationData.value = null;
  modelError.value = false;
  imageError.value = false;
  if (!props.url) {
    loading.value = false;
    return;
  }
  try {
    const isModel = props.badgeStyle === "THREE_DIMENSION";
    const result = await loadBadgeAssets(
      props.url,
      isModel,
      request.signal,
      (value) => (progress.value = value)
    );
    if (request.signal.aborted) return;
    animationData.value = result.animation;
    if (isModel) modelBuffer.value = result.media;
    else objectUrl.value = URL.createObjectURL(result.media);
  } catch (error) {
    if (request.signal.aborted) return;
    modelError.value = true;
  } finally {
    if (!request.signal.aborted) loading.value = false;
  }
}

watch(() => [props.url, props.badgeStyle, props.fallbackUrl], load, {
  immediate: true,
});
onBeforeUnmount(release);
</script>

<style lang="scss" scoped>
.badge-large {
  position: relative;
  height: 720rpx;
  flex: none;

  &__status,
  &__image {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  &__status {
    flex-direction: column;
    gap: 16rpx;
    font-size: 24rpx;
    font-weight: 600;

    button {
      padding: 16rpx 24rpx;
      border: 0;
      border-radius: 12rpx;
      background: var(--du-bg-1, #fff);
      color: inherit;
      cursor: pointer;
    }
  }

  &__image img {
    display: block;
    width: 514rpx;
    height: 514rpx;
    object-fit: contain;
  }
}
</style>
